import type {ReactElement} from 'react';
import {cloneElement} from 'react';

import styled from '@emotion/styled';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {ModalStepAnimation} from 'UI/animations/modals';

import {useModalStepper} from './useModalStepper';

/**
 * The `ComponentProps` interface defines the properties required for the `ModalStepper` component.
 * This interface ensures type safety for components that need to display multiple steps within a modal interface with smooth animations.
 * The component automatically handles step navigation and passes navigation handlers to child components for seamless user interaction.
 */
interface ComponentProps {
    /**
     * An array of React elements representing each step in the modal stepper.
     * Each element will be rendered as a separate step with animation transitions between them.
     * Navigation handlers (handleNext and handleBack) are automatically injected into each child component.
     */
    children: ReactElement<any>[];
    /**
     * The test identifier for the Modal Stepper component used in testing scenarios.
     * This property is crucial for uniquely identifying the component during automated testing.
     *
     * @default 'ModalStepper'
     */
    testId?: string;
}

/**
 * The `ModalStepper` component provides a comprehensive solution for managing and displaying multiple steps within a modal interface.
 * It handles step navigation with smooth animations and automatically passes navigation handlers to child components.
 * The component uses Framer Motion for seamless transitions between steps and maintains the current step state internally.
 *
 * @param props          The component props.
 * @param props.children An array of React elements representing each step in the modal stepper.
 * @param props.testId   The test identifier for the Modal Stepper component used in testing scenarios.
 * @returns The rendered ModalStepper component with animated step transitions.
 *
 * @example
 * ```tsx
 * <ModalStepper testId="registration-stepper">
 *     <StepOne />
 *     <StepTwo />
 *     <StepThree />
 * </ModalStepper>
 * ```
 */
const ModalStepper = ({children, testId = 'ModalStepper'}: ComponentProps) => {
    const {direction, handleBack, handleNext, step} = useModalStepper(children);

    return (
        <Wrapper data-cy={testId}>
            <AnimatePresence custom={direction} initial={false} mode="popLayout">
                <AnimationWrapper
                    key={step}
                    animate="enter"
                    custom={direction}
                    exit="exit"
                    initial="initial"
                    variants={ModalStepAnimation}
                >
                    {cloneElement(children[Number(step)], {
                        handleBack,
                        handleNext
                    })}
                </AnimationWrapper>
            </AnimatePresence>
        </Wrapper>
    );
};

ModalStepper.displayName = 'ModalStepper';

export {ModalStepper};

const Wrapper = styled(motion.div)`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    height: 100%;
    width: 100%;
`;

const AnimationWrapper = styled(motion.div)`
    display: grid;
    grid-column-start: 1;
    grid-row-start: 1;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    height: auto;
    max-height: 100%;
    min-height: 100%;
`;